import React from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, MapPinIcon, VideoCameraIcon, UserIcon } from '@heroicons/react/24/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '@theraply/ui';
import { TherapistResult } from '../../hooks/useTherapistSearch';

interface TherapistCardProps {
  therapist: TherapistResult;
  onBookNow?: (therapistId: number) => void;
}

/**
 * Therapist card component for search results.
 *
 * Features:
 * - Photo with fallback
 * - Name and credentials
 * - Specialties tags
 * - Rating with stars
 * - Rate per session
 * - Distance from search location
 * - Session type icons
 * - Next available slot
 * - View profile and Book now actions
 *
 * Performance:
 * - Lazy loaded images
 * - Memoized to prevent unnecessary re-renders
 */
export const TherapistCard = React.memo<TherapistCardProps>(({ therapist, onBookNow }) => {
  const {
    id,
    firstName,
    lastName,
    bio,
    profilePhotoS3Key,
    specialties = [],
    sessionTypes = [],
    ratePerSession,
    rating = 0,
    reviewCount = 0,
    yearsOfExperience,
    languages = [],
    city,
    state,
    distanceMiles,
    nextAvailableSlot,
  } = therapist;

  const fullName = `${firstName} ${lastName}`;
  const location = city && state ? `${city}, ${state}` : null;

  // Format rating to 1 decimal place
  const formattedRating = rating.toFixed(1);

  // Format rate
  const formattedRate = `$${ratePerSession.toFixed(0)}/session`;

  // Format next available slot
  const formattedNextAvailable = nextAvailableSlot
    ? new Date(nextAvailableSlot).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      })
    : 'Contact for availability';

  // Session type indicators
  const hasVideo = sessionTypes.includes('video');
  const hasInPerson = sessionTypes.includes('in_person');

  // Render star rating
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarIcon key={i} className="h-4 w-4 text-yellow-400 fill-current" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarIcon className="h-4 w-4 text-gray-300" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
            </div>
          </div>
        );
      } else {
        stars.push(
          <StarIcon key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }

    return stars;
  };

  const handleBookClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onBookNow?.(id);
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="p-6">
        {/* Header: Photo and Basic Info */}
        <div className="flex gap-4 mb-4">
          {/* Profile Photo */}
          <div className="flex-shrink-0">
            {profilePhotoS3Key ? (
              <img
                src={`https://your-s3-bucket.s3.amazonaws.com/${profilePhotoS3Key}`}
                alt={fullName}
                className="h-20 w-20 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center">
                <UserIcon className="h-10 w-10 text-indigo-600" />
              </div>
            )}
          </div>

          {/* Name, Rating, Location */}
          <div className="flex-1 min-w-0">
            <Link
              to={`/therapists/${id}`}
              className="text-lg font-semibold text-gray-900 hover:text-indigo-600 transition-colors"
            >
              {fullName}
            </Link>

            {/* Rating */}
            <div className="flex items-center gap-2 mt-1">
              <div className="flex items-center">
                {renderStars()}
              </div>
              <span className="text-sm text-gray-600">
                {formattedRating} ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
              </span>
            </div>

            {/* Location and Distance */}
            {(location || distanceMiles !== undefined) && (
              <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                <MapPinIcon className="h-4 w-4" />
                <span>
                  {location}
                  {distanceMiles !== undefined && ` • ${distanceMiles} miles away`}
                </span>
              </div>
            )}
          </div>

          {/* Rate */}
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold text-indigo-600">${ratePerSession.toFixed(0)}</div>
            <div className="text-sm text-gray-500">per session</div>
          </div>
        </div>

        {/* Bio */}
        {bio && (
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {bio}
          </p>
        )}

        {/* Specialties */}
        {specialties.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {specialties.slice(0, 5).map((specialty) => (
                <span
                  key={specialty}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  {specialty.replace('_', ' ')}
                </span>
              ))}
              {specialties.length > 5 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  +{specialties.length - 5} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Session Types, Experience, Languages */}
        <div className="flex flex-wrap items-center gap-4 mb-4 text-sm text-gray-600">
          {/* Session Types */}
          <div className="flex items-center gap-2">
            {hasVideo && (
              <div className="flex items-center gap-1" title="Video sessions">
                <VideoCameraIcon className="h-4 w-4 text-indigo-600" />
                <span>Video</span>
              </div>
            )}
            {hasInPerson && (
              <div className="flex items-center gap-1" title="In-person sessions">
                <UserIcon className="h-4 w-4 text-indigo-600" />
                <span>In-person</span>
              </div>
            )}
          </div>

          {/* Experience */}
          {yearsOfExperience && (
            <span>{yearsOfExperience} years experience</span>
          )}

          {/* Languages */}
          {languages.length > 0 && (
            <span>{languages.join(', ')}</span>
          )}
        </div>

        {/* Next Available Slot */}
        {nextAvailableSlot && (
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
            <ClockIcon className="h-4 w-4" />
            <span>Next available: {formattedNextAvailable}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Link to={`/therapists/${id}`} className="flex-1">
            <Button variant="outline" className="w-full">
              View Profile
            </Button>
          </Link>
          <Button
            variant="primary"
            className="flex-1"
            onClick={handleBookClick}
          >
            Book Now
          </Button>
        </div>
      </div>
    </div>
  );
});

TherapistCard.displayName = 'TherapistCard';
